import type { IconName } from '@travelnest/hui';

import { formatTime } from '@/lib/dates';
import type { IRestriction } from '@/types/content';

export interface IHouseRuleDisplay {
	key: string;
	label: string;
	icon: IconName;
}

type RuleConfig = Omit<IHouseRuleDisplay, 'key'>;

/** The restriction types that are house rules (the others are fees). */
const RULE_TYPES = [
	'smoking',
	'pets',
	'children allowed',
	'infants allowed',
	'group bookings',
] as const;

type RuleType = (typeof RULE_TYPES)[number];

const ALLOWED: Record<RuleType, RuleConfig & { key: string }> = {
	smoking: {
		key: 'smoking_allowed',
		label: 'Smoking allowed',
		icon: 'Smoking',
	},
	pets: { key: 'pets_allowed', label: 'Pets allowed', icon: 'Dog' },
	'children allowed': {
		key: 'children_allowed',
		label: 'Children allowed',
		icon: 'Person',
	},
	'infants allowed': {
		key: 'infants_allowed',
		label: 'Infants allowed',
		icon: 'Pram',
	},
	'group bookings': {
		key: 'groups_allowed',
		label: 'Group bookings allowed',
		icon: 'Party',
	},
};

const NOT_ALLOWED: Record<RuleType, RuleConfig & { key: string }> = {
	smoking: { key: 'no_smoking', label: 'No smoking', icon: 'NoSmoking' },
	pets: { key: 'no_pets', label: 'Pets not allowed', icon: 'NoDog' },
	'children allowed': {
		key: 'no_children',
		label: 'Children not allowed',
		icon: 'NoPerson',
	},
	'infants allowed': {
		key: 'no_infants',
		label: 'Infants not allowed',
		icon: 'NoPram',
	},
	'group bookings': {
		key: 'no_groups',
		label: 'No parties / events allowed',
		icon: 'NoParty',
	},
};

const isRuleType = (type: string): type is RuleType =>
	(RULE_TYPES as readonly string[]).includes(type);

/**
 * Every house rule, allowed or not, plus the check-in and check-out times.
 * A rule the property has not set is shown as "not allowed", which is the
 * platform's default.
 */
export const mapHouseRules = (
	restrictions: IRestriction[],
	checkIn: string,
	checkOut: string,
): IHouseRuleDisplay[] => {
	const allowed = new Set(
		restrictions
			.filter((r) => r.allowed && isRuleType(r.restriction_type))
			.map((r) => r.restriction_type),
	);

	return [
		{
			key: 'check_in',
			label: `Check in: ${formatTime(checkIn)}`,
			icon: 'DoorEnter',
		},
		{
			key: 'check_out',
			label: `Check out: ${formatTime(checkOut)}`,
			icon: 'DoorExit',
		},
		...RULE_TYPES.map((type) =>
			allowed.has(type) ? ALLOWED[type] : NOT_ALLOWED[type],
		),
	];
};
